import { PdfButton } from "../components/PdfButton";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PAGE_LABELS, type PageKey } from "@owners/shared-types";
import { money, moneyCompact, num, pct, pts } from "@owners/format";
import { api, useMe } from "../api";
import { PATHS } from "../routes";
import { MoneyVar, PtsVar, Tile, useHotels } from "../components/Numbers";
import { PeopleTraining } from "../components/PeopleTraining";

/**
 * Today's Update (SPEC §2) — the landing page. Aggregates other sections only.
 * Live in Phase 1: occupancy and RevPAR yesterday (nightly close), NOI last
 * month vs budget (the books), Needs you this week and Coming up (Dataroom
 * requirements). Full-year reforecast, loans below covenant and the Q4
 * distribution need Debt Compliance and Budget + Reforecast (Phase 2) — TBD.
 */

interface Numbers {
  asOf: string; hotels: number;
  yesterday: null | { occupancy: number | null; revpar: number | null; adr: number | null; roomsSold: number; hotelsReporting: number;
    lastYear: null | { occupancy: number | null; revpar: number | null } };
  noiLastMonth: null | { label: string; actual: number | null; budget: number | null; lastYear: number | null; hotelsReporting: number };
  noiFullYearReforecast: null | { projected: number; budget: number | null; year: number; actualMonths: number; hotelsReporting: number };
  q4Distribution: null | { available: number | null; contributionsNeeded?: number; contributionTotal?: number | null; inputsMissing?: number };
  loansBelowCovenant: null | { count: number; debt: number; watch: number; loans: number; through: string | null };
  flashError?: string; pnlError?: string;
}
interface AgendaItem { kind?: string; text: string; date: string | null; page: string; query: string }
interface Agenda { needsYou: AgendaItem[]; comingUp: AgendaItem[] }

export function TodaysUpdate() {
  const { data: me } = useMe();
  const nav = useNavigate();
  const hotels = useHotels().data?.hotels ?? [];
  const n = useQuery({ queryKey: ["today-numbers"], queryFn: () => api<Numbers>("/api/today/numbers") });
  const a = useQuery({ queryKey: ["today-agenda"], queryFn: () => api<Agenda>("/api/today/agenda") });
  const can = (p: PageKey) => !!me?.pages.includes(p);
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }).toUpperCase();
  const y = n.data?.yesterday;
  const noi = n.data?.noiLastMonth;
  const adminNoPreview = me?.level === "admin" && !me.previewing;

  return (
    <>
      {!adminNoPreview && <PdfButton fileName="Today's Update" />}
      <div className="eyebrow">{today} · {adminNoPreview ? "ADMIN" : `ALL ${hotels.length} HOTELS`}</div>
      <h1>Good {greeting()}, {me?.name.split(" ")[0]}.</h1>

      {adminNoPreview ? (
        <div className="card muted">Admins see client data by previewing a level (left menu).</div>
      ) : (
        <>
          <div className="tiles strip">
            <Tile label="Occupancy yesterday" tbd={!n.isLoading && !y}
              value={n.isLoading ? "…" : pct(y?.occupancy, { ratio: true })}
              sub={y && <><PtsVar actual={y.occupancy} base={y.lastYear?.occupancy ?? null} /> vs last year · {num(y.hotelsReporting)} of {num(n.data!.hotels)} hotels</>}
              onClick={can("flash_reports") ? () => nav(PATHS.flash_reports) : undefined} />
            <Tile label="RevPAR yesterday" tbd={!n.isLoading && !y}
              value={n.isLoading ? "…" : money(y?.revpar, { decimals: 2 })}
              sub={y && <><MoneyVar actual={y.revpar} base={y.lastYear?.revpar ?? null} /> vs last year · ADR {money(y.adr, { decimals: 2 })}</>}
              onClick={can("flash_reports") ? () => nav(PATHS.flash_reports) : undefined} />
            <Tile label={noi ? `NOI · ${noi.label}` : "NOI last month vs budget"} tbd={!n.isLoading && !noi}
              value={n.isLoading ? "…" : moneyCompact(noi?.actual)}
              sub={noi && <><MoneyVar actual={noi.actual} base={noi.budget} /> vs budget · {num(noi.hotelsReporting)} hotels</>}
              onClick={can("financial_reports") ? () => nav(PATHS.financial_reports) : undefined} />
            <Tile label={`Full-year NOI reforecast${n.data?.noiFullYearReforecast ? ` · ${n.data.noiFullYearReforecast.year}` : ""}`}
              tbd={!n.isLoading && !n.data?.noiFullYearReforecast}
              value={n.isLoading ? "…" : moneyCompact(n.data?.noiFullYearReforecast?.projected)}
              sub={n.data?.noiFullYearReforecast && <><MoneyVar actual={n.data.noiFullYearReforecast.projected} base={n.data.noiFullYearReforecast.budget} /> vs budget · actual through {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Math.max(n.data.noiFullYearReforecast.actualMonths - 1, 0)]}</>}
              onClick={can("budget_reforecast") ? () => nav(PATHS.budget_reforecast) : undefined} />
            <Tile label="Loans below covenant" tbd={!n.isLoading && !n.data?.loansBelowCovenant}
              value={n.isLoading ? "…" : num(n.data?.loansBelowCovenant?.count)}
              sub={n.data?.loansBelowCovenant ? (n.data.loansBelowCovenant.count
                ? `${moneyCompact(n.data.loansBelowCovenant.debt)} of debt · ${num(n.data.loansBelowCovenant.loans)} loans tested`
                : `All ${num(n.data.loansBelowCovenant.loans)} loans pass${n.data.loansBelowCovenant.watch ? ` · ${num(n.data.loansBelowCovenant.watch)} on watch` : ""}`)
                : "Enter loans in Debt Compliance"}
              onClick={can("debt_compliance") ? () => nav(PATHS.debt_compliance) : undefined} />
            <Tile label="Q4 distribution available" tbd={!n.isLoading && (n.data?.q4Distribution?.available ?? null) === null}
              value={n.isLoading ? "…" : moneyCompact(n.data?.q4Distribution?.available)}
              sub={n.data?.q4Distribution?.available != null
                ? (n.data.q4Distribution.contributionsNeeded ? `${num(n.data.q4Distribution.contributionsNeeded)} hotels need an owner contribution` : "After debt service, reserves and CapEx")
                : n.data?.q4Distribution?.inputsMissing ? `Owner CapEx / distributions not entered for ${num(n.data.q4Distribution.inputsMissing)} hotels` : "Arrives with Budget + Reforecast"}
              onClick={can("budget_reforecast") ? () => nav(PATHS.budget_reforecast) : undefined} />
          </div>

          {y && <p className="narrative">{narrative(n.data!)}</p>}

          <GoodNews items={goodNews(n.data, a.data)} />

          <div className="row" style={{ alignItems: "flex-start", gap: 18 }}>
            <div className="card" style={{ flex: 3, minWidth: 340 }}>
              <h2>Needs you this week</h2>
              {a.isLoading ? <p className="muted">Loading…</p> : !a.data?.needsYou.length ? <p className="muted">Nothing needs you this week.</p> : (
                <ol className="needs">
                  {a.data.needsYou.map((i, k) => (
                    <li key={k} className="need">
                      <span className="need-n">{k + 1}</span>
                      <div className="need-body">
                        <div className="need-tags">
                          <span className="need-tag">{PAGE_LABELS[i.page as PageKey] ?? i.page}</span>
                          <span className={`pill ${i.kind === "expired" || i.kind === "missing" ? "bad-pill" : "warn-pill"}`}>{label(i.kind)}{i.date ? ` ${shortDay(i.date)}` : ""}</span>
                        </div>
                        <div className="need-text">{i.text}</div>
                      </div>
                      <AgendaLink i={i} cta />
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div className="card" style={{ flex: 2, minWidth: 300 }}>
              <h2>Coming up</h2>
              {a.isLoading ? <p className="muted">Loading…</p> : !a.data?.comingUp.length ? <p className="muted">Nothing due in the next 60 days.</p> : (
                <ol className="timeline">
                  {a.data.comingUp.map((i, k) => (
                    <li key={k}>
                      <span className="tl-date"><b>{dayNum(i.date!)}</b>{monthAbbr(i.date!)}</span>
                      <div className="tl-body"><AgendaLink i={i} /><div className="muted tl-when">{relDays(i.date!)}</div></div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          <PeopleTraining />
        </>
      )}
    </>
  );
}

function AgendaLink({ i, cta }: { i: AgendaItem; cta?: boolean }) {
  const path = PATHS[i.page as PageKey];
  if (!path) return cta ? null : <span>{i.text}</span>;
  const to = `${path}${i.query ? `?${i.query}` : ""}`;
  return cta ? <Link className="need-cta" to={to}>Open →</Link> : <Link to={to}>{i.text}</Link>;
}

/** Good news (demo): only facts already on this page — nothing invented, nothing shown if there's nothing good. */
export function goodNews(d: Numbers | undefined, a: Agenda | undefined): Array<{ text: string; page: PageKey }> {
  if (!d) return [];
  const out: Array<{ text: string; page: PageKey }> = [];
  const y = d.yesterday;
  if (y?.occupancy != null && y.lastYear?.occupancy != null && y.occupancy > y.lastYear.occupancy)
    out.push({ page: "flash_reports", text: `Occupancy yesterday ran ${pts((y.occupancy - y.lastYear.occupancy) * 100)} ahead of the same day last year.` });
  if (y?.revpar != null && y.lastYear?.revpar != null && y.revpar > y.lastYear.revpar && y.lastYear.revpar > 0)
    out.push({ page: "flash_reports", text: `RevPAR was up ${pct(((y.revpar - y.lastYear.revpar) / y.lastYear.revpar) * 100)} on last year.` });
  const noi = d.noiLastMonth;
  if (noi?.actual != null && noi.budget != null && noi.actual > noi.budget)
    out.push({ page: "financial_reports", text: `${noi.label} NOI beat budget by ${moneyCompact(noi.actual - noi.budget)}.` });
  const fy = d.noiFullYearReforecast;
  if (fy?.budget != null && fy.projected > fy.budget)
    out.push({ page: "budget_reforecast", text: `${fy.year} NOI is reforecast ${moneyCompact(fy.projected - fy.budget)} ahead of budget.` });
  const l = d.loansBelowCovenant;
  if (l && l.loans > 0 && l.count === 0)
    out.push({ page: "debt_compliance", text: `All ${num(l.loans)} loans pass their covenant tests.` });
  const q4 = d.q4Distribution;
  if (q4?.available != null && q4.available > 0 && !q4.contributionsNeeded)
    out.push({ page: "budget_reforecast", text: `${moneyCompact(q4.available)} is available for the Q4 distribution.` });
  if (a && a.needsYou.length === 0)
    out.push({ page: "reo_documents", text: "Every required document is on file and current." });
  return out;
}

function GoodNews({ items }: { items: Array<{ text: string; page: PageKey }> }) {
  const { data: me } = useMe();
  const shown = items.filter((g) => me?.pages.includes(g.page));
  if (!shown.length) return null;
  return (
    <div className="card good-news">
      <h2>Good news</h2>
      <ul>
        {shown.map((g, k) => <li key={k}><Link to={PATHS[g.page]}>{g.text}</Link></li>)}
      </ul>
    </div>
  );
}

const label = (k?: string) => (k === "expired" ? "Expired" : k === "missing" ? "Missing" : "Due");

/** One factual paragraph from the numbers on the page — no invented reasons. */
function narrative(d: Numbers): string {
  const y = d.yesterday!;
  const parts: string[] = [];
  const occ = pct(y.occupancy, { ratio: true });
  if (y.lastYear?.occupancy != null && y.occupancy != null) {
    const delta = (y.occupancy - y.lastYear.occupancy) * 100;
    parts.push(`Yesterday the portfolio ran ${occ} occupancy, ${pts(delta)} ${delta >= 0 ? "ahead of" : "behind"} the same day last year, at a ${money(y.adr, { decimals: 2 })} ADR.`);
  } else {
    parts.push(`Yesterday the portfolio ran ${occ} occupancy at a ${money(y.adr, { decimals: 2 })} ADR.`);
  }
  const noi = d.noiLastMonth;
  if (noi?.actual != null && noi.budget != null) {
    const diff = noi.actual - noi.budget;
    parts.push(`${noi.label} NOI was ${moneyCompact(noi.actual)}, ${moneyCompact(Math.abs(diff))} ${diff >= 0 ? "ahead of" : "short of"} budget.`);
  }
  return parts.join(" ");
}

const shortDay = (iso: string) => new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const dayNum = (iso: string) => new Date(iso + "T12:00:00Z").getUTCDate();
const monthAbbr = (iso: string) => new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
function relDays(iso: string): string {
  const t = new Date(); const today = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  const k = Math.round((Date.parse(iso + "T00:00:00Z") - today) / 86_400_000);
  return k === 0 ? "Today" : k === 1 ? "Tomorrow" : k > 0 ? `In ${k} days` : `${-k} days ago`;
}

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
}
